import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Select,
  DatePicker,
  Row,
  Col,
  message,
  Modal,
  Tooltip,
  Alert,
} from "antd";
import {
  EyeOutlined,
  DollarOutlined,
  ReloadOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";

const { RangePicker } = DatePicker;
const { Option } = Select;

function PaymentsTable({ onViewDetails }) {
  const [modal, contextHolder] = Modal.useModal();
  const [filters, setFilters] = useState({
    pageNumber: 1,
    payment_status: "all",
    verification_status: "all",
    from_date: null,
    to_date: null,
  });

  // API hooks
  const [paymentsResponse, getPayments] = useApi(
    "/admin/payments/all",
    null,
    API_METHODS.GET
  );
  const [authorizeResponse, authorizePayment] = useApi();

  // Fetch payments
  const fetchPayments = () => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "all" && value !== "") {
        queryParams.append(key, value);
      }
    });
    getPayments(
      null,
      API_METHODS.GET,
      `/admin/payments/all?${queryParams.toString()}`
    );
  };

  useEffect(() => {
    fetchPayments();
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, pageNumber: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({
      ...prev,
      pageNumber: page,
    }));
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setFilters((prev) => ({
      ...prev,
      from_date: dates ? dates[0].format("YYYY-MM-DD") : null,
      to_date: dates ? dates[1].format("YYYY-MM-DD") : null,
      pageNumber: 1,
    }));
  };

  // Handle payment authorization
  const handleAuthorizePayment = (paymentId) => {
    modal.confirm({
      title: "Authorize Payment",
      okText: "Authorize",
      okButtonProps: {
        style: { backgroundColor: "#f97316", borderColor: "#f97316", color: "#fff" },
      },
      content: (
        <div>
          <p>Are you sure you want to manually authorize this payment?</p>
          <Alert
            message="Important"
            description="Please verify this payment in your Razorpay dashboard before authorizing. This action will confirm the order and clear the customer's cart."
            type="warning"
            showIcon
            style={{ marginTop: "12px" }}
          />
        </div>
      ),
      onOk: () => {
        authorizePayment(
          { admin_confirmed: true },
          API_METHODS.POST,
          `/admin/payments/authorize/${paymentId}`
        );
      },
    });
  };

  // Success handlers
  useEffect(() => {
    if (authorizeResponse.data && !authorizeResponse.isLoading) {
      message.success("Payment authorized successfully");
      fetchPayments();
    }
  }, [authorizeResponse]);

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString("en-IN")}`;
  };

  // Table columns
  const columns = [
    {
      title: "Payment Details",
      key: "payment_details",
      render: (record) => (
        <div>
          <div
            style={{
              fontWeight: "500",
              fontFamily: "monospace",
              fontSize: "12px",
            }}
          >
            {record.razorpay_payment_id || "N/A"}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            Order:{" "}
            <Tag size="small" color="blue">
              {record.order_number}
            </Tag>
          </div>
          <div style={{ fontSize: "10px", color: "#999" }}>ID: {record.id}</div>
        </div>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      render: (record) => (
        <div>
          <div style={{ fontWeight: "500" }}>{record.user_name}</div>
          <div style={{ fontSize: "12px", color: "#666" }}>{record.email}</div>
          {record.phone && (
            <div style={{ fontSize: "12px", color: "#666" }}>
              📞 {record.phone}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (
        <div style={{ fontWeight: "600", color: "#f97316", fontSize: "16px" }}>
          {formatCurrency(amount)}
        </div>
      ),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "Payment Status",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (status) => {
        const statusConfig = {
          pending: { color: "orange", text: "PENDING" },
          completed: { color: "green", text: "COMPLETED" },
          failed: { color: "red", text: "FAILED" },
          refunded: { color: "purple", text: "REFUNDED" },
        };
        const config = statusConfig[status] || {
          color: "default",
          text: status?.toUpperCase(),
        };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: "Verification",
      key: "verification_status",
      render: (record) => (
        <div>
          <div style={{ marginBottom: "4px" }}>
            <Tag
              color={record.frontend_verified ? "green" : "red"}
              size="small"
            >
              Frontend: {record.frontend_verified ? "VERIFIED" : "NO"}
            </Tag>
          </div>
          {/* <div>
            <Tag color={record.webhook_verified ? "green" : "red"} size="small">
              Webhook: {record.webhook_verified ? "VERIFIED" : "NO"}
            </Tag>
          </div> */}
        </div>
      ),
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      render: (method) => method || "Online",
    },
    {
      title: "Payment Date",
      dataIndex: "payment_date",
      key: "payment_date",
      render: (date) => new Date(date).toLocaleDateString("en-IN"),
      // sorter: (a, b) => new Date(a.created_on) - new Date(b.created_on),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space>
          <Tooltip title="View Payment Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onViewDetails && onViewDetails(record)}
              style={{ backgroundColor: "#f97316", borderColor: "#f97316", color: "#fff" }}
            />
          </Tooltip>
          {!record.frontend_verified && !record.webhook_verified && (
              <Tooltip title="Authorize Payment">
                <Button
                  type="default"
                  size="small"
                  icon={<SafetyOutlined />}
                  onClick={() => handleAuthorizePayment(record.payment_id)}
                  loading={authorizeResponse.isLoading}
                  style={{ color: "#fa8c16", borderColor: "#fa8c16" }}
                >
                  Authorize
                </Button>
              </Tooltip>
            )}
        </Space>
      ),
    },
  ];


  return (
    <div style={{ padding: "24px" }}>
      {contextHolder}
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <DollarOutlined style={{ marginRight: "8px", color: "#f97316" }} />
            Payment Management
          </div>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={fetchPayments}>
            Refresh
          </Button>
        }
      >
        {/* Filters */}
        <div
          style={{
            marginBottom: "16px",
            padding: "16px",
            backgroundColor: "#fafafa",
            borderRadius: "6px",
          }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Payment Status"
                value={filters.payment_status}
                onChange={(value) =>
                  handleFilterChange("payment_status", value)
                }
                style={{ width: "100%" }}
              >
                <Option value="all">All Status</Option>
                <Option value="pending">Pending</Option>
                <Option value="completed">Completed</Option>
                <Option value="failed">Failed</Option>
                <Option value="refunded">Refunded</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Verification Status"
                value={filters.verification_status}
                onChange={(value) =>
                  handleFilterChange("verification_status", value)
                }
                style={{ width: "100%" }}
              >
                <Option value="all">All Verification</Option>
                <Option value="verified">Verified</Option>
                <Option value="unverified">Needs Authorization</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <RangePicker
                placeholder={["From Date", "To Date"]}
                onChange={handleDateRangeChange}
                style={{ width: "100%" }}
              />
            </Col>
          </Row>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={paymentsResponse.data || []}
          loading={paymentsResponse.isLoading}
          rowKey="id"
          pagination={{
            current: filters.pageNumber,
            total: paymentsResponse.pagination?.count || 0,
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} payments`,
            onChange: (page) => {
              handlePageChange(page);
              const table = document.querySelector(".ant-table");
              if (table) {
                table.scrollIntoView({ behavior: "smooth", block: "start" });
              } else {
                // Fallback: scroll to top of page
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            },
          }}
          scroll={{ x: 1000 }}
          size="small"
          rowClassName={(record) => {
            if (
              record.payment_status === "completed" &&
              !record.frontend_verified && !record.webhook_verified
            ) {
              return "needs-authorization-row";
            }
            return "";
          }}
        />
      </Card>

      <style jsx>{`
        .needs-authorization-row {
          background-color: #fff7e6 !important;
        }
      `}</style>
    </div>
  );
}

export default PaymentsTable;
